function homeContent() {
  return {
    eyebrow: 'BotSpot AI trading workspace',
    heading: 'Hire an AI agent to run your trading.',
    summary: 'Turn trading rules into inspectable code, run historical simulations, and connect a supported broker without giving BotSpot custody of your funds.',
    sections: [
      {
        heading: 'Build, backtest, and operate in one workspace',
        paragraphs: [
          'BotSpot is an AI trading-bot builder and backtesting platform. Describe entries, exits, sizing, and risk in plain English. The agent turns those rules into editable Lumibot strategy code you can read before anything runs.',
          'Historical simulations are labeled as simulated. They can include return, drawdown, trades, and logs for a date range you choose. A backtest is not live trading and does not guarantee future results.',
          'When you are ready, connect a supported brokerage account and approve paper or live operation. You keep funds at the broker. BotSpot does not take custody, and it does not place trades until you approve the run.',
        ],
      },
      {
        heading: 'What you can do after you start',
        bullets: [
          'Describe a strategy and inspect the generated code.',
          'Run a historical simulation and read trades, drawdown, and logs.',
          'Clone a public marketplace strategy and customize it in your account.',
          'Connect supported brokers for paper or live operation after you approve the settings.',
          'Use ChatGPT, Claude, Cursor, Codex, or another MCP client with the same BotSpot tools.',
        ],
      },
      {
        heading: 'Pricing without invented offers',
        paragraphs: [
          'Public BotSpot software plans start at the Light subscription and scale through Starter, Pro, and All Access. Current advertised software prices are published on the pricing page and in the public pricing source. Business capacity is quoted separately. Education products are separate paid offers.',
          'BotSpot core software is paid upfront. This page does not advertise a free product trial. Capturing an email is not a substitute for a paid plan.',
        ],
      },
      {
        heading: 'Risk',
        paragraphs: [
          'Automated trading can lose money. Simulated backtests, paper trading, and past live history do not guarantee future performance. BotSpot is software for automating rules you inspect. It is not an investment adviser and does not promise returns.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See BotSpot plans', url: '/pricing' },
      { label: 'Browse marketplace strategies', url: '/marketplace' },
      { label: 'Compare BotSpot with other platforms', url: '/compare' },
    ],
  };
}

function salesContent() {
  return {
    eyebrow: 'Start with BotSpot',
    heading: 'Build AI trading bots in minutes',
    summary: 'Create an account, describe a trading idea, inspect the generated strategy, and backtest it before you connect a broker.',
    sections: [
      {
        heading: 'From a prompt to an inspectable bot',
        paragraphs: [
          'BotSpot is for people who want their own rules running at their own broker. You describe the idea. The AI Agent drafts strategy code you can read. You run a historical simulation, review trades and drawdown, and only then decide whether to connect a supported account.',
          'The sales path is the paid-upfront BotSpot software purchase. It is not a free trading product, a signal service that hides the rules, or a promise that a generated bot will profit.',
        ],
      },
      {
        heading: 'What you keep control of',
        bullets: [
          'Strategy code and revisions in your account.',
          'Backtest date range, logs, and simulated metrics.',
          'Broker connection, paper versus live mode, and start approval.',
          'Risk limits you set before a bot is allowed to run.',
        ],
      },
      {
        heading: 'Risk',
        paragraphs: [
          'Trading involves risk of loss. Backtests are simulations. Paper trading is not live trading. BotSpot does not guarantee returns.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Compare plans', url: '/pricing' },
      { label: 'Open the algorithm marketplace', url: '/marketplace' },
      { label: 'MCP for Agents', url: '/agents' },
    ],
  };
}

function marketplaceHubContent() {
  return {
    eyebrow: 'Public strategy catalog',
    heading: 'Algorithm Marketplace',
    summary: 'Browse public BotSpot trading strategies, read each listing’s published rules and simulated metrics, then clone a strategy into your own account to inspect it.',
    sections: [
      {
        heading: 'A catalog of inspectable strategy templates',
        paragraphs: [
          'The BotSpot algorithm marketplace is a public catalog of trading-bot templates. Each listing can publish a title, description, asset classes, simulated backtest fields, and live history from connected bots. You open a listing, read what the publisher actually wrote, and clone the strategy into your account if you want to inspect the rules.',
          'Marketplace pages are not a ranking of “best bots,” a guaranteed-return leaderboard, or a managed-account shop. BotSpot does not invent equity curves for listings that omit them. If a listing has no drawdown or date range, that evidence is missing.',
          'Cloning copies the published strategy into your workspace. It does not start live trading. You still choose whether to simulate, whether to connect a broker, and whether to approve paper or live mode.',
        ],
      },
      {
        heading: 'How to read a listing without fooling yourself',
        paragraphs: [
          'Treat backtest CAGR, Sharpe, and max drawdown as historical simulations. When the listing includes a window, read the return next to that window and next to drawdown. A high simulated return with an unpublished drawdown is incomplete evidence.',
          'Live figures, when present, describe published connected-bot history for that listing. Live history can still lose money, can include different brokers and sizes, and is not a promise that your clone will match it.',
          'Use the listing description and details to understand entries, exits, sizing, and risk. If those fields are thin, clone the strategy and read the code rather than filling the gaps with marketing language.',
        ],
        bullets: [
          'Prefer listings that publish rules, simulated drawdown, and a date range together.',
          'Label every backtest number as simulated when you discuss it.',
          'Do not treat marketplace sort order as investment advice.',
          'Run your own simulation after cloning before you consider live mode.',
        ],
      },
      {
        heading: 'What happens after you clone',
        paragraphs: [
          'In your BotSpot account you can revise the strategy, backtest it on dates you choose, and keep it stopped. Connecting a supported broker still requires your approval. Funds stay at the broker.',
          'Paid marketplace listings, when priced, are separate from BotSpot software plans. Software capacity for live bots, backtest hours, and AI revisions is described on the pricing page. This hub does not invent a listing price.',
        ],
      },
      {
        heading: 'Risk',
        paragraphs: [
          'Every marketplace strategy can lose money. Simulated results and past live history do not guarantee future performance. BotSpot is not an investment adviser. Use the catalog to inspect templates, not to outsource judgment.',
        ],
      },
    ],
    faq: [
      {
        question: 'Are marketplace backtests live results?',
        answer: 'No. Backtest figures on marketplace listings are historical simulations. Live figures, when shown, are published connected-bot history for that listing. Neither is a guarantee of future results.',
      },
      {
        question: 'Does cloning a strategy start trading?',
        answer: 'No. Cloning copies the published strategy into your account so you can inspect and test it. Nothing trades until you connect a broker and approve a run.',
      },
    ],
    relatedLinks: [
      { label: 'Compare BotSpot plans', url: '/pricing' },
      { label: 'Read backtesting metrics', url: '/guides/backtesting-metrics-explained' },
      { label: 'Algorithmic trading risk guide', url: '/guides/algorithmic-trading-risk-management' },
    ],
  };
}

function pricingContent() {
  return {
    eyebrow: 'BotSpot plans',
    heading: 'AI trading bot pricing and plans',
    summary: 'Compare BotSpot software plans for AI strategy building, historical simulation hours, live bots, broker connections, and support.',
    sections: [
      {
        heading: 'Paid software capacity, published from one source',
        paragraphs: [
          'BotSpot software plans are paid subscriptions. Public prices for Light, Starter, Pro, and All Access come from the same pricing source used by this page and by generated site artifacts. Business capacity is custom. Education products are separate paid offers.',
          'Plans differ by live-bot capacity, backtesting hours, strategy revisions, AI credits, and support. They do not sell a guaranteed return. Backtests remain simulations on every plan.',
        ],
      },
      {
        heading: 'What you are paying for',
        bullets: [
          'AI Agent workflow to draft and revise inspectable strategy code.',
          'Historical simulation hours to review trades, drawdown, and logs.',
          'Capacity to run approved live bots at a supported broker.',
          'Marketplace access to clone public strategy templates.',
          'Support and Live Algo Lab access on higher plans as published on this page.',
        ],
      },
      {
        heading: 'Risk',
        paragraphs: [
          'Paying for software does not reduce market risk. Automated trading can lose money. BotSpot does not offer a free core SaaS plan on this page.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Start with BotSpot', url: '/sales' },
      { label: 'Browse marketplace strategies', url: '/marketplace' },
      { label: 'Enterprise inquiries', url: '/enterprise' },
    ],
  };
}

function blogIndexContent() {
  return {
    eyebrow: 'BotSpot writing',
    heading: 'Latest BotSpot articles',
    summary: 'Read BotSpot market newsletters, automated trading tutorials, AI strategy guides, and product updates.',
    sections: [
      {
        heading: 'What this blog publishes',
        paragraphs: [
          'The BotSpot blog collects product updates, automation tutorials, and market notes written for people who build and operate trading bots. Articles are editorial pages, not personalized investment advice.',
          'When a post discusses backtests, treat those figures as simulations unless the article clearly describes live history, including date range and drawdown. Nothing on the blog guarantees future returns.',
        ],
      },
      {
        heading: 'How to use the archive',
        bullets: [
          'Open an article for the full canonical URL under /blog/{slug}.',
          'Use tutorials alongside the in-app AI Agent and backtest workspace.',
          'Use product updates to see current workflow changes, then confirm behavior in the app.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Open the AI Agent', url: '/ai-agent' },
      { label: 'Algorithm marketplace', url: '/marketplace' },
      { label: 'Trading guides', url: '/guides' },
    ],
  };
}

const CORE_PUBLIC_PAGE_CONTENT = {
  '/': homeContent(),
  '/sales': salesContent(),
  '/marketplace': marketplaceHubContent(),
  '/pricing': pricingContent(),
  '/blog': blogIndexContent(),
};

function corePublicPageContent(pathname) {
  return CORE_PUBLIC_PAGE_CONTENT[pathname] || null;
}

module.exports = {
  CORE_PUBLIC_PAGE_CONTENT,
  corePublicPageContent,
};
